import { createSelector, Selector } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import {
  OrderUpdate,
  OrderDetail,
  OrderStatus,
  EntityType,
  CollectionDetail,
  Order,
  TradingEntityWithAlias,
  TradingEntity,
  TradeConditionFlag,
  CounterpartyInstruction,
  PrimeBroker
} from 'api'
import { orderStoreSelector } from './storeSelectors'
import { createSelectedCollectionIdSelector } from './selectionSelectors'
import { RepositoryEntry } from 'store/repository'
import { currentOrganisationIdSelector, isOwnTradingEntityGetterSelector } from './adminSelectors'
import { createEntityGetterSelector } from './entitySelectors'
import {
  applyUpdatesForWindow,
  UnassignedCollectionId,
  toCollectionDetails,
  UnassignedCollectionName,
  newCollectionDetail,
  sortCreatedAtDescAndUploadRowIndexAsc
} from 'model'
import { getCurrentWindowId } from 'utils'
import { GridId } from 'store/grid'
import { Locale, Translation } from '../locale'
import { showOrgActivitySelector } from './gridSelectors'
import { createAuthorisedUserSelector } from './connectionSelectors'
import { isNil } from 'lodash-es'
import { isNotNil } from 'utils/predicate'

interface OrderSelectionOptions {
  statuses?: OrderStatus[]
  collectionIdSelector?: Selector<GlobalState, string | null>
}

const stagingOrderStatuses: OrderStatus[] = [OrderStatus.INCOMPLETE, OrderStatus.READY, OrderStatus.PREPARED]

export function createOrderEntrySelector(options: OrderSelectionOptions): Selector<GlobalState, RepositoryEntry<OrderUpdate>[]> {
  const { statuses } = options
  const collectionIdSelector = options.collectionIdSelector || (() => null)
  return createSelector(
    orderStoreSelector,
    currentOrganisationIdSelector,
    collectionIdSelector,
    showOrgActivitySelector,
    createAuthorisedUserSelector(),
    (orderStore, currentOrganisationId, collectionIdOrUnassigned, showOrgActivity, authorisedUser) => {
      const allOrderUpdates = Object.values(orderStore).filter(entry => {
        const order = entry.lastUpdate.entity

        if (collectionIdOrUnassigned) {
          const collectionId = collectionIdOrUnassigned === UnassignedCollectionId ? null : collectionIdOrUnassigned
          if (order.containerCollectionId !== collectionIdOrUnassigned && order.collectionId !== collectionId) {
            return false
          }
        }

        if (currentOrganisationId && order.initiatorOrganisationId !== currentOrganisationId) {
          return false
        }

        if (statuses && !statuses.includes(order.status)) {
          return false
        }

        if (showOrgActivity) {
          return true
        }

        return order.createdBy === authorisedUser.username
      })

      return sortCreatedAtDescAndUploadRowIndexAsc(allOrderUpdates)
    }
  )
}

export const counterpartyNameGetterSelector: Selector<GlobalState, (order: Order) => TradingEntityWithAlias | null> = createSelector(
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  isOwnTradingEntityGetterSelector,
  (getTradingEntity, getIsOwnTradingEntity) => (order: Order) => {
    const counterpartyInstructions = order.negotiationInstruction.counterpartyInstructions

    if (counterpartyInstructions.length === 0) {
      return null
    }

    if (counterpartyInstructions.length > 1) {
      return {
        publishedName: Locale.translate(Translation.SC_MULTIPLE)
      } as TradingEntity
    }

    if (!order.fundId) {
      return null
    }

    const ownOrder = getIsOwnTradingEntity(order.fundId)
    const counterpartyId = counterpartyInstructions[0].counterpartyId

    const tradingEntity = getTradingEntity(counterpartyId)
    const alias = counterpartyInstructions[0]?.counterpartyAlias

    const counterparty = counterpartyId ? (ownOrder ? ({ ...tradingEntity, alias } as TradingEntityWithAlias) : tradingEntity) : null

    return counterparty
  }
)

const axeIdFromOrderGetterSelector = createSelector(
  [createEntityGetterSelector(EntityType.TRADING_ENTITY)],
  getTradingEntity => (order: Order) => {
    let axeId = null
    const {
      negotiationInstruction: { counterpartyInstructions }
    } = order

    const counterpartyInstructionsWithAxeIds = counterpartyInstructions.filter(c => isNotNil(c.axeId))

    if (counterpartyInstructionsWithAxeIds.length > 1) {
      axeId = Locale.translate(Translation.SC_MULTIPLE)
    } else if (counterpartyInstructionsWithAxeIds.length === 1) {
      const [first] = counterpartyInstructionsWithAxeIds
      if (counterpartyInstructions.length > 1) {
        const entity = isNotNil(first) && getTradingEntity(first.counterpartyId)
        if (!!entity) {
          axeId = `${entity.publishedName} - ${first.axeId}`
        }
      } else {
        axeId = first.axeId
      }
    }
    return axeId
  }
)
export function createOrderDetailsSelector(options: OrderSelectionOptions): Selector<GlobalState, OrderDetail[]> {
  return createSelector(
    createOrderEntrySelector(options),
    createEntityGetterSelector(EntityType.COLLATERAL),
    createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
    createEntityGetterSelector(EntityType.TRADING_ENTITY),
    createEntityGetterSelector(EntityType.COLLECTION),
    createEntityGetterSelector(EntityType.PRIME_BROKER),
    createEntityGetterSelector(EntityType.PRODUCT),
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    createEntityGetterSelector(EntityType.REFERENCE_RATE),
    createEntityGetterSelector(EntityType.USER),
    counterpartyNameGetterSelector,
    createEntityGetterSelector(EntityType.CLEARING_HOUSE),
    createEntityGetterSelector(EntityType.SPONSOR),
    axeIdFromOrderGetterSelector,
    (
      entries,
      getCollateral,
      getCollateralBasket,
      getTradingEntity,
      getCollection,
      getPrimeBroker,
      getProduct,
      getProductSector,
      getReferenceRate,
      getUser,
      getCounterpartyName,
      getClearingHouse,
      getSponsor,
      getAxeId
    ) => {
      return entries.map(entry => {
        const order: Order = applyUpdatesForWindow(entry, getCurrentWindowId())
        const validationIssues = entry.lastUpdate.validationIssues
        const availableEvents = entry.lastUpdate.availableEvents
        const collateral = order.collateralId ? getCollateral(order.collateralId) : null
        const collateralBasket = order.basketAllocation ? getCollateralBasket(order.basketAllocation.basketId) : null
        const orderFund = getOrderFund(order, getTradingEntity)
        const collection = order.collectionId ? getCollection(order.collectionId) : null
        const containerCollection = order.containerCollectionId ? getCollection(order.containerCollectionId) : null
        const userSelectedPrimeBroker = (order.primeBrokerId && getPrimeBroker(order.primeBrokerId)) ?? null

        const primeBroker =
          userSelectedPrimeBroker ||
          getCounterpartyInstructionPrimeBroker(order.negotiationInstruction.counterpartyInstructions, getPrimeBroker)
        const productSector = order.productSectorId ? getProductSector(order.productSectorId) : null
        const product = order.productId ? getProduct(order.productId) : null
        const referenceRate = order.referenceRate ? getReferenceRate(order.referenceRate) : null
        const floatCrystallisationDays = order.floatCrystallisationDays
        const counterparty = getCounterpartyName(order)

        const counterpartyInstructions = order.negotiationInstruction.counterpartyInstructions
        const axeTradeCondition = {
          isAxed: counterpartyInstructions.length === 1 ? counterpartyInstructions[0].isAxed : false,
          tradeCondition: counterpartyInstructions.length === 1 ? counterpartyInstructions[0].tradeConditionFlag : TradeConditionFlag.NONE
        }
        const investmentDecisionMaker = order.investmentDecisionMaker ? getUser(order.investmentDecisionMaker) : null
        const executionDecisionMaker = order.executionDecisionMaker ? getUser(order.executionDecisionMaker) : null

        const secondaryCollateral = order.secondaryCollateralId ? getCollateral(order.secondaryCollateralId) : null
        const secondaryCollateralBasket = order.secondaryBasketId ? getCollateralBasket(order.secondaryBasketId) : null

        const secondaryInstrumentName = secondaryCollateralBasket
          ? secondaryCollateralBasket.name
          : secondaryCollateral
          ? secondaryCollateral.name
          : null

        const clearingHouseName = order.negotiationInstruction.clearingHouseId
          ? getClearingHouse(order.negotiationInstruction.clearingHouseId)?.publishedName ?? null
          : null
        const sponsoringBankName = order.negotiationInstruction.sponsorId
          ? getSponsor(order.negotiationInstruction.sponsorId)?.publishedName ?? null
          : null

        const isLinked = order.servicingTrigger?.linked.negotiated
        const linkedTradeId = isLinked ? order.servicingTrigger?.previousDealId : null

        const counterpartyIds = counterpartyInstructions.map(counterpartyInstruction => counterpartyInstruction.counterpartyId)

        return {
          order,
          axeId: getAxeId(order),
          collateral,
          collateralBasket,
          secondaryCollateral,
          secondaryCollateralBasket,
          productSector,
          product,
          collection,
          containerCollection,
          orderFund,
          primeBroker,
          referenceRate,
          floatCrystallisationDays,
          counterparty,
          axeTradeCondition,
          investmentDecisionMaker,
          executionDecisionMaker,
          validationIssues,
          availableEvents,
          secondaryInstrumentName,
          clearingHouseName,
          sponsoringBankName,
          linkedTradeId,
          counterpartyIds
        } as OrderDetail
      })
    }
  )
}

function getOrderFund(order: Order, getTradingEntity: (id: string) => TradingEntity | null): TradingEntity | null {
  if (!order.fundId) return null

  const fund = getTradingEntity(order.fundId)

  const hasPreAssignments = order?.nestedLegs?.legs?.length

  return fund && hasPreAssignments ? { ...fund, publishedName: Locale.translate(Translation.TC_PRE_ASSIGNED) } : fund
}

function getCounterpartyInstructionPrimeBroker(
  counterpartyInstructions: CounterpartyInstruction[],
  getPrimeBroker: (id: string) => PrimeBroker | null
): PrimeBroker | null {
  // If null, then all prime broker Ids are null
  const firstPrimeBrokerId = counterpartyInstructions.find(counterpartyInstruction => counterpartyInstruction.primeBrokerId)?.primeBrokerId

  if (isNil(firstPrimeBrokerId)) {
    return null
  }

  const firstPrimeBroker = getPrimeBroker(firstPrimeBrokerId)

  if (counterpartyInstructions.every(counterpartyInstruction => counterpartyInstruction.primeBrokerId === firstPrimeBrokerId)) {
    return firstPrimeBroker
  }

  return firstPrimeBroker ? { ...firstPrimeBroker, name: Locale.translate(Translation.SC_MULTIPLE) } : null
}

export function createStagingOrderDetailsSelector(): Selector<GlobalState, OrderDetail[]> {
  return createOrderDetailsSelector({
    statuses: stagingOrderStatuses,
    collectionIdSelector: createSelectedCollectionIdSelector(GridId.StagingGrid)
  })
}

export const createOrderDetailGetterSelector: Selector<
  GlobalState,
  (orderId: string) => OrderDetail | null
> = createSelector(createOrderDetailsSelector({}), allOrderDetails => (orderId: string) =>
  allOrderDetails.find(orderDetail => orderDetail.order.orderId === orderId) ?? null
)

export function createOrderCollectionDetailsSelector(
  selectedCollectionIdSelector: Selector<GlobalState, string | null>,
  statuses: OrderStatus[]
): Selector<GlobalState, CollectionDetail[]> {
  return createSelector(
    [
      createOrderEntrySelector({ statuses }),
      createEntityGetterSelector(EntityType.COLLECTION),
      selectedCollectionIdSelector,
      currentOrganisationIdSelector
    ],
    (orders, collectionGetter, selectedCollectionId, currentOrganisationId) => {
      // if in admin global view don't list collections
      if (!currentOrganisationId) {
        return []
      }

      const collectionRecords = orders.reduce((previousCollectionRecords, orderUpdate) => {
        const order = orderUpdate.lastUpdate.entity

        const candidateCollectionId = order.containerCollectionId ?? order.collectionId

        // if collection id assigned but collection is not available don't add it
        const collection = candidateCollectionId ? collectionGetter(candidateCollectionId) : null
        if (candidateCollectionId && !collection) {
          return previousCollectionRecords
        }

        const collectionId = candidateCollectionId || UnassignedCollectionId
        const collectionName = collection ? collection.name : UnassignedCollectionName

        if (!previousCollectionRecords[collectionId]) {
          previousCollectionRecords[collectionId] = newCollectionDetail(collectionId, collectionName)
        }

        const previousCollectionDetail = previousCollectionRecords[collectionId]
        previousCollectionDetail.totalItems += 1
        if (order.status === OrderStatus.READY) {
          previousCollectionDetail.subTotals[0] += 1
        } else if (order.status === OrderStatus.INCOMPLETE) {
          previousCollectionDetail.subTotals[1] += 1
        } else if (order.status === OrderStatus.PREPARED) {
          previousCollectionDetail.subTotals[2] += 1
        }

        return previousCollectionRecords
      }, {} as Record<string, CollectionDetail>)

      return toCollectionDetails(collectionRecords, selectedCollectionId, collectionGetter)
    }
  )
}

export const stagingOrdersCollectionDetailsSelector = createOrderCollectionDetailsSelector(
  createSelectedCollectionIdSelector(GridId.StagingGrid),
  stagingOrderStatuses
)

export const stagingOrdersNumberOfItemsSelector: Selector<GlobalState, number> = createSelector(
  createOrderEntrySelector({
    statuses: stagingOrderStatuses
  }),
  orderEntries => orderEntries.length
)

export const createOrderIdsWithCollectionIdGetterSelector: Selector<
  GlobalState,
  (collectionId: string) => string[]
> = createSelector(createOrderEntrySelector({ statuses: stagingOrderStatuses }), orderEntries => (collectionId: string) =>
  orderEntries.filter(orderEntry => orderEntry.lastUpdate.entity.collectionId === collectionId).map(orderEntry => orderEntry.entityId)
)

export const createOrdersWithCollectionIdGetterSelector: Selector<
  GlobalState,
  (collectionId: string) => Order[]
> = createSelector(createOrderEntrySelector({ statuses: stagingOrderStatuses }), orderEntries => (collectionId: string) =>
  orderEntries
    .filter(orderEntry => orderEntry.lastUpdate.entity.collectionId === collectionId)
    .map(orderEntry => orderEntry.lastUpdate.entity)
)

export const orderIdsWithContainerCollectionIdGetterSelector: Selector<
  GlobalState,
  (containerCollectionId: string) => string[]
> = createSelector(createStagingOrderDetailsSelector(), orderDetails => {
  return (containerCollectionId: string) => {
    return orderDetails.flatMap(d => (d?.containerCollection?.id === containerCollectionId ? [d.order.orderId] : []))
  }
})
